import {
  Route,
  Outlet,
  Routes,
  Navigate,
  BrowserRouter,
} from 'react-router-dom';

import { EUserRole } from '@/enum';
import AppLayout from '@/layout/appLayout';
import AuthLayout from '@/layout/authLayout';
import CheckYourEmail from '@/pages/common/auth/CheckYourEmail';
import ForgotPassword from '@/pages/common/auth/ForgotPassword';
import Login from '@/pages/common/auth/Login';
import PasswordSuccess from '@/pages/common/auth/PasswordSuccess';
import ResetPassword from '@/pages/common/auth/ResetPassword';
import GameDashboard from '@/pages/common/gameDashboard';
import HelpSupport from '@/pages/common/helpSupport';
import MaintenancePage from '@/pages/common/maintenance';
import ParentControls from '@/pages/parent/controls';
import ParentDashboard from '@/pages/parent/dashboard';
import ParentReportsAnalytics from '@/pages/parent/reportsAnalytics';
import ParentSettings from '@/pages/parent/settings';
import ClassDetails from '@/pages/school/classDetails';
import SchoolLessonBreakdownList from '@/pages/school/classDetails/LessonBreakdownList';
import ClassTeachersList from '@/pages/school/classTeachersList';
import Controls from '@/pages/school/controls';
import Dashboard from '@/pages/school/dashboard';
import Lessons from '@/pages/school/lessons';
import SchoolLessonDetails from '@/pages/school/lessons/SchoolLessonDetails';
import ReportsAnalytics from '@/pages/school/reportsAnalytics';
import Settings from '@/pages/school/settings';
import Students from '@/pages/school/students';
import AssignedClasses from '@/pages/school/teacherAssignedClassList/AssignedClasses';
import TeacherDetails from '@/pages/school/teacherDetails';
import TeachersAndClasses from '@/pages/school/teachersAndClasses';
import TeacherClasses from '@/pages/teacher/Classes';
import LessonBreakdownList from '@/pages/teacher/Classes/LessonBreakdownList';
import TeacherClassDetails from '@/pages/teacher/Classes/TeacherClassDetails';
import TeacherDashboard from '@/pages/teacher/dashboard';
import TeacherAssignedClasses from '@/pages/teacher/dashboard/AssignedClasses';
import TeacherLessons from '@/pages/teacher/Lessons';
import TeacherLessonDetails from '@/pages/teacher/Lessons/TeacherLessonDetails';
import TeacherReports from '@/pages/teacher/Reports';
import TeacherSettings from '@/pages/teacher/settings';

import MaintenanceGuard from './MaintenanceGuard';
import { PublicRoute, PrivateRoute } from './RouteGuard';

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <MaintenanceGuard>
        <Routes>
          <Route path="/maintenance" element={<MaintenancePage />} />

          <Route
            element={
              <PublicRoute>
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              </PublicRoute>
            }
          >
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/check-email" element={<CheckYourEmail />} />
            <Route path="/success" element={<PasswordSuccess />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Route>

          <Route
            element={
              <PrivateRoute>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </PrivateRoute>
            }
          >
            <Route path="/help-support" element={<HelpSupport />} />
          </Route>

          <Route
            path="school"
            element={
              <PrivateRoute permission={EUserRole.SCHOOL}>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </PrivateRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="reports-analytics" element={<ReportsAnalytics />} />
            <Route path="controls" element={<Controls />} />
            <Route path="students" element={<Students />} />
            <Route path="teachers-classes" element={<TeachersAndClasses />} />
            <Route path="lessons" element={<Lessons />} />
            <Route path="lessons/:id" element={<SchoolLessonDetails />} />
            <Route path="settings" element={<Settings />} />
            <Route
              path="teachers-classes/teacher-details/:id"
              element={<TeacherDetails />}
            />
            <Route
              path="teachers-classes/teacher-details/:id/assigned-classes"
              element={<AssignedClasses />}
            />
            <Route
              path="teachers-classes/teacher-details/:id/class/:classId"
              element={<ClassDetails />}
            />
            <Route
              path="teachers-classes/class-details/:classId"
              element={<ClassDetails />}
            />
            <Route
              path="teachers-classes/class-details/:classId/teachers"
              element={<ClassTeachersList />}
            />
            <Route
              path="teachers-classes/class-details/:classId/lesson-breakdown"
              element={<SchoolLessonBreakdownList />}
            />
            {/* Fallback inside the school group */}
            <Route
              path="*"
              element={<Navigate to="/school/dashboard" replace />}
            />
          </Route>

          <Route
            path="parent"
            element={
              <PrivateRoute permission={EUserRole.PARENT}>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </PrivateRoute>
            }
          >
            <Route path="dashboard" element={<ParentDashboard />} />
            <Route
              path="reports-analytics"
              element={<ParentReportsAnalytics />}
            />
            <Route path="controls" element={<ParentControls />} />
            <Route path="settings" element={<ParentSettings />} />
          </Route>

          <Route
            path="teacher"
            element={
              <PrivateRoute permission={EUserRole.TEACHER}>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </PrivateRoute>
            }
          >
            <Route path="dashboard" element={<TeacherDashboard />} />
            <Route
              path="dashboard/assigned-classes"
              element={<TeacherAssignedClasses />}
            />
            <Route path="reports-analytics" element={<TeacherReports />} />
            <Route path="lessons" element={<TeacherLessons />} />
            <Route path="lessons/create" element={<GameDashboard />} />
            <Route path="lessons/:id" element={<TeacherLessonDetails />} />
            <Route path="lessons/:id/add-game" element={<GameDashboard />} />
            <Route path="classes" element={<TeacherClasses />} />
            <Route path="classes/:id" element={<TeacherClassDetails />} />
            <Route path="classes/:id/add-game" element={<GameDashboard />} />
            <Route
              path="classes/:id/create-lesson"
              element={<GameDashboard />}
            />
            <Route
              path="classes/:id/lesson-breakdown"
              element={<LessonBreakdownList />}
            />
            <Route path="settings" element={<TeacherSettings />} />
          </Route>
        </Routes>
      </MaintenanceGuard>
    </BrowserRouter>
  );
};

export default AppRoutes;
